import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Calendar, User } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function Blog() {
  const { t, i18n } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: 'Blog',
        page_path: window.location.pathname,
      });
    }
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/blog/posts`);
      setPosts(response.data);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  const isFR = i18n.language === 'fr';
  const loc = (fr, en) => (isFR && fr) ? fr : en;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(isFR ? 'fr-CA' : 'en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="blog-page" data-testid="blog-page">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-warm-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="overline mb-4">{t('blog.title')}</div>
          <h1 className="font-playfair text-5xl md:text-7xl tracking-tighter leading-[1.1] font-medium text-charcoal mb-6" data-testid="blog-title">
            {t('blog.heading')}
          </h1>
          <p className="text-lg md:text-xl leading-relaxed text-charcoal/80">
            {t('blog.subtitle')}
          </p>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-16 md:py-24 bg-warm-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-charcoal/60 text-lg" data-testid="blog-empty">{t('blog.empty')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-3xl overflow-hidden shadow-[0_8px_32px_rgba(59,48,45,0.04)] border border-accent/30 hover:shadow-[0_16px_48px_rgba(59,48,45,0.08)] transition-shadow duration-300"
                  data-testid={`blog-post-${post.id}`}
                >
                  <div className="h-56 overflow-hidden">
                    <img 
                      src={post.hero_image || 'https://images.unsplash.com/photo-1611173622933-91942d394b04?w=800'}
                      alt={loc(post.title_fr, post.title)}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center space-x-4 text-sm text-muted-text mb-4">
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(post.created_at)}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <User className="w-4 h-4" />
                        <span>{post.author}</span>
                      </div>
                    </div>
                    <h3 className="font-playfair text-2xl font-medium text-charcoal mb-3 line-clamp-2">
                      {loc(post.title_fr, post.title)}
                    </h3>
                    <p className="text-charcoal/80 leading-relaxed mb-4 line-clamp-3">
                      {loc(post.excerpt_fr, post.excerpt)}
                    </p>
                    <Link 
                      to={`/blog/${post.slug || post.id}`}
                      className="inline-flex items-center text-primary hover:text-charcoal transition-colors font-medium"
                      data-testid={`read-more-${post.id}`}
                    >
                      {t('blog.readMore')} →
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default Blog;